import React, {ReactNode, useState, useEffect, useRef} from "react"
import {
	Box,
	Button,
	TextField,
	Typography,
	FormControlLabel,
	Checkbox,
	CircularProgress,
	Alert,
	Divider,
} from "@mui/material"

import {useDraftReply} from "@indebted/api/conversationDraftReplyAPI"
import {useSendReply} from "@indebted/api/conversationSendReplyAPI"
import type {DraftReplyBlocked, DraftReplyDraft, EmailRecipient} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"

type DraftReplyPanelProps = {
	customerID: string
	conversationID: string
	providerMessageId: string
	repliesToEarlierMessage?: boolean
	onSent?: () => void
}

type Result =
	| {kind: "ok"; draft: DraftReplyDraft; omittedFields: string[]}
	| {kind: "blocked"; reason: DraftReplyBlocked["reason"]; caseType?: string}

type SendState = {kind: "idle"} | {kind: "sent"} | {kind: "blocked"; caseType?: string} | {kind: "error"}

const FACT_FIELDS = [
	{label: "Name", field: "customer_name"},
	{label: "Balance", field: "outstanding_balance"},
	{label: "Last payment amount", field: "last_payment_amount"},
	{label: "Last payment date", field: "last_payment_date"},
	{label: "Original creditor", field: "original_creditor"},
	{label: "Payment link", field: "payment_portal_link"},
]

function DraftReplyPanel({
	customerID,
	conversationID,
	providerMessageId,
	repliesToEarlierMessage,
	onSent,
}: DraftReplyPanelProps) {
	const {notification} = useNotification()
	const {mutateAsync, isPending} = useDraftReply()
	const {mutateAsync: sendReply, isPending: isSending} = useSendReply()
	const [agentEmail, agentName] = useAgent()

	const [guidance, setGuidance] = useState("")
	const [fields, setFields] = useState<string[]>([])
	const [replyInCustomerLanguage, setReplyInCustomerLanguage] = useState(true)
	const [result, setResult] = useState<Result | null>(null)
	const [sendState, setSendState] = useState<SendState>({kind: "idle"})

	const mounted = useRef(true)
	useEffect(() => {
		return () => {
			mounted.current = false
		}
	}, [])

	function toggleField(field: string, checked: boolean) {
		setFields((current) => (checked ? [...current, field] : current.filter((f) => f !== field)))
	}

	async function generateDraft() {
		setSendState({kind: "idle"})
		try {
			const response = await mutateAsync({
				customerID,
				conversationID,
				providerMessageId,
				guidance,
				fields,
				replyInCustomerLanguage,
			})
			if (response.status === "ok") {
				setResult({kind: "ok", draft: response.draft, omittedFields: response.omittedFields ?? []})
			} else {
				setResult({kind: "blocked", reason: response.reason, caseType: response.caseType})
			}
		} catch {
			notification.error("Failed to generate the draft reply")
		}
	}

	async function handleApproveAndSend() {
		if (result?.kind !== "ok") return
		try {
			const response = await sendReply({
				customerID,
				conversationID,
				providerMessageId,
				subject: result.draft.subject,
				html: result.draft.html,
				text: result.draft.text,
				guidance,
				authoredBy: agentEmail ?? "",
				modelVersion: result.draft.modelVersion,
			})
			if (!mounted.current) return
			if (response.status === "accepted") {
				setSendState({kind: "sent"})
				onSent?.()
			} else {
				setSendState({kind: "blocked", caseType: response.caseType})
			}
		} catch {
			if (mounted.current) setSendState({kind: "error"})
		}
	}

	return (
		<PanelColumn>
			<Typography variant="subtitle2" color="textPrimary">
				Draft a reply
			</Typography>
			{repliesToEarlierMessage && (
				<Alert severity="warning" sx={{borderRadius: 2}}>
					Replying to an earlier message — the customer has sent newer messages since.
				</Alert>
			)}
			<TextField
				label="Instructions"
				placeholder="Tell the assistant how to reply"
				value={guidance}
				onChange={(event) => setGuidance(event.target.value)}
				multiline
				minRows={3}
				fullWidth
				size="small"
			/>

			<FieldSet label="Include these customer details in the reply">
				<Box display="flex" gap={2} flexWrap="wrap">
					{FACT_FIELDS.map(({label, field}) => (
						<FormControlLabel
							key={field}
							control={
								<Checkbox
									checked={fields.includes(field)}
									onChange={(event) => toggleField(field, event.target.checked)}
								/>
							}
							label={label}
						/>
					))}
				</Box>
			</FieldSet>

			<FieldSet label="Reply language">
				<FormControlLabel
					control={
						<Checkbox
							checked={replyInCustomerLanguage}
							onChange={(event) => setReplyInCustomerLanguage(event.target.checked)}
						/>
					}
					label="Reply in the customer's language"
				/>
			</FieldSet>

			<Button
				variant="contained"
				color="primary"
				aria-label="Generate draft"
				disabled={isPending}
				onClick={generateDraft}
				sx={{alignSelf: "flex-start"}}
			>
				{isPending ? <CircularProgress size={24} /> : "Generate draft"}
			</Button>
			{result && <Divider />}
			{result?.kind === "ok" &&
				(sendState.kind === "sent" ? (
					<Alert severity="success" sx={{borderRadius: 2}}>
						Reply sent by {agentName ?? agentEmail}
					</Alert>
				) : (
					<DraftPreview
						draft={result.draft}
						omittedFields={result.omittedFields}
						onApproveAndSend={handleApproveAndSend}
						isSending={isSending}
						canSend={Boolean(agentEmail)}
						sendState={sendState}
					/>
				))}
			{result?.kind === "blocked" && <BlockedBanner reason={result.reason} caseType={result.caseType} />}
		</PanelColumn>
	)
}

function PanelColumn({children}: {children: ReactNode}) {
	return (
		<Box display="flex" flexDirection="column" gap={2}>
			{children}
		</Box>
	)
}

function FieldSet({label, children}: {label: string; children: ReactNode}) {
	return (
		<Box
			display="flex"
			flexDirection="column"
			gap={1}
			border="1px solid"
			borderColor="grey.200"
			borderRadius={1}
			p={1.5}
		>
			<Typography variant="caption" color="textSecondary" textTransform="uppercase" letterSpacing={0.5}>
				{label}
			</Typography>
			{children}
		</Box>
	)
}

function DraftPreview({
	draft,
	omittedFields,
	onApproveAndSend,
	isSending,
	canSend,
	sendState,
}: {
	draft: DraftReplyDraft
	omittedFields: string[]
	onApproveAndSend: () => void
	isSending: boolean
	canSend: boolean
	sendState: SendState
}) {
	const omittedLabels = omittedFields
		.map((field) => FACT_FIELDS.find((fact) => fact.field === field)?.label)
		.filter((label): label is string => Boolean(label))

	return (
		<PanelColumn>
			<Box display="flex" flexDirection="column" gap={1}>
				<Typography variant="subtitle2" color="textPrimary">
					Drafted email
				</Typography>
				<RecipientRow label="From" recipient={draft.from} />
				<RecipientRow label="To" recipient={draft.to} />
				<Typography variant="body2" color="textPrimary" fontWeight={600}>
					{draft.subject}
				</Typography>
				<Typography variant="body2" color="textPrimary" sx={{whiteSpace: "pre-wrap"}}>
					{draft.text}
				</Typography>
			</Box>

			{omittedLabels.length > 0 && (
				<Alert severity="info" sx={{borderRadius: 2}}>
					Not included — no data on file: {omittedLabels.join(", ")}
				</Alert>
			)}

			{sendState.kind === "blocked" && (
				<Alert severity="warning" sx={{borderRadius: 2}}>
					{sendState.caseType
						? `Can't send — active ${sendState.caseType} case.`
						: "Can't send — an active case blocks this reply."}
				</Alert>
			)}
			{sendState.kind === "error" && (
				<Alert severity="error" sx={{borderRadius: 2}}>
					{"Couldn't send the reply. Try again."}
				</Alert>
			)}

			<Button
				variant="contained"
				color="primary"
				aria-label="Approve & send"
				disabled={!canSend || isSending}
				onClick={onApproveAndSend}
				sx={{alignSelf: "flex-start"}}
			>
				{isSending ? <CircularProgress size={24} /> : "Approve & send"}
			</Button>
			{!canSend && (
				<Typography variant="caption" color="textSecondary">
					Sign in to send replies.
				</Typography>
			)}
		</PanelColumn>
	)
}

function RecipientRow({label, recipient}: {label: string; recipient: EmailRecipient}) {
	return (
		<Typography variant="caption" color="textSecondary">
			{label}: {recipient.label ? `${recipient.label} <${recipient.email}>` : recipient.email}
		</Typography>
	)
}

function BlockedBanner({reason, caseType}: {reason: DraftReplyBlocked["reason"]; caseType?: string}) {
	const message =
		reason === "contact_constraint"
			? caseType
				? `An active ${caseType} case blocks this reply.`
				: "An active case on this customer blocks this reply."
			: reason === "prompt_injection"
				? "This reply was blocked for a suspected prompt injection in the customer message."
				: "The automated-conduct budget for this customer is exhausted, so no automated reply can be drafted."

	return (
		<Alert severity="warning" sx={{borderRadius: 2}}>
			{message}
		</Alert>
	)
}

export {DraftReplyPanel}
